import React, { useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import LiveChatContextMenu from 'flow/components/context-menus/LiveChatContextMenu';
import BaseListItem, {
    rowMarking,
} from 'flow/components/ui-components/BaseListItem';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import UnreadBadge from 'flow/components/ui-components/UnreadBadge';
import useAppContext from 'flow/hooks/useAppContext';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ChatSession } from 'flow/types/Chats';
import { ChannelKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getFormattedTime, getIdFromEntityKey } from 'flow/utils';
import {
    getWidgetChatSenderName,
    messageIsFromExtension,
} from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    chatSession: TypedMap<ChatSession>;
}

const WidgetLatestMessage = ({ chatSession }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { entityKey: channelKey } = useParams<{ entityKey: ChannelKey }>();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const chatSessionKey = chatSession.get('key');
    const hasUnreadMessages = chatSession.get('numberOfUnreadMessages') > 0;

    const message = chatSession.get('latestMessage');
    const sentTime = message && getFormattedTime(message.get('sent'), i18next);

    return (
        <BaseListItem
            marking={rowMarking({ highlighted: contextMenuIsActive })}
            className="badge-bg"
            onClick={() =>
                history.push(
                    generatePath(
                        routes['pbx.channels.channelKey.chatSessionKey'],
                        { channelKey, chatSessionKey },
                    ),
                )
            }
        >
            <div className="relative flex items-center">
                <ContactAvatar contact={message?.getIn(['from', 'contact'])} />
                <UnreadBadge session={chatSession} />
            </div>

            <div className="flex-1 truncate">
                <div className="flex flex-col justify-between gap-2 truncate">
                    <p
                        className={twMerge(
                            'flex truncate text-base/5',
                            hasUnreadMessages ? 'font-bold' : '',
                        )}
                    >
                        <div
                            className={twMerge(
                                'rounded-md border-2 px-3 py-1 text-xs tracking-wider',
                                hasUnreadMessages
                                    ? 'border-gray-500 text-gray-800'
                                    : 'border-gray-300 text-gray-500',
                            )}
                        >
                            {'#' + getIdFromEntityKey(chatSession.get('key'))}
                        </div>
                    </p>

                    <div
                        className={twMerge(
                            'flex flex-row items-center text-sm',
                            hasUnreadMessages ? 'font-bold' : '',
                            messageIsFromExtension(message, ownExtensionKey)
                                ? 'text-chatYou'
                                : 'text-gray-800',
                        )}
                    >
                        {getWidgetChatSenderName(
                            message,
                            ownExtensionKey,
                            i18next,
                        )}
                        : &nbsp;
                        <p
                            dir="auto"
                            className="truncate text-sm text-gray-500"
                        >
                            {message?.get('message')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex h-full justify-end self-start">
                <div
                    className={twMerge(
                        'flex h-full flex-col gap-2',
                        contextMenuIsActive
                            ? 'hidden'
                            : 'group-focus-visible-within:hidden group-hover:hidden',
                    )}
                >
                    <div className="text-end text-xs/5 text-nowrap text-gray-500">
                        {sentTime}
                    </div>

                    <div className="flex justify-end gap-x-1">
                        {chatSession.get('chatArchived', false) && (
                            <div className="mt-auto flex size-5 items-center justify-center self-end rounded-sm bg-gray-200">
                                <Icon
                                    size="16"
                                    iconKey="archive"
                                    className="text-gray-500"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div
                className={twMerge(
                    contextMenuIsActive
                        ? 'flex'
                        : 'sr-only-until-hover-or-focus flex',
                )}
            >
                <LiveChatContextMenu
                    channelKey={channelKey}
                    chatSession={chatSession}
                    setActive={setContextMenuIsActive}
                />
            </div>
        </BaseListItem>
    );
};

export default WidgetLatestMessage;
